import { SEMANTIC_COLORS } from '@/constants/colors.constant'
import { RiskDataItem } from '@/services/whatIfAnalysis.service'
import { Box, Typography } from '@mui/material'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useMemo } from 'react'

interface RiskDataProps {
  data?: RiskDataItem[]
  loading?: boolean
  error?: string | null
}

const RiskData: React.FC<RiskDataProps> = ({ 
  data = [], 
  loading = false, 
  error = null,
}) => {
  
  
  const chartOptions = useMemo(() => {
    let beforeData, afterData

    if (!data || data.length === 0) {
      beforeData = {
        total_risk: 0, yield_risk: 0, sector_risk: 0, quality_risk: 0, currency_risk: 0, other_risk: 0,
        total_risk_b: 0, yield_risk_b: 0, sector_risk_b: 0, quality_risk_b: 0, currency_risk_b: 0, other_risk_b: 0
      }
      afterData = beforeData
    } else {
      beforeData = data.find(item => item.is_before === true)
      afterData = data.find(item => item.is_before === false)
      
      if (!beforeData || !afterData) {
        beforeData = afterData = {
          total_risk: 0, yield_risk: 0, sector_risk: 0, quality_risk: 0, currency_risk: 0, other_risk: 0,
          total_risk_b: 0, yield_risk_b: 0, sector_risk_b: 0, quality_risk_b: 0, currency_risk_b: 0, other_risk_b: 0
        }
      }
    }

    return {
      chart: {
        type: 'column',
        height: 500
      },
      credits: {
        enabled: false
      },
      title: {
        text: ''
      },
      xAxis: {
        categories: ['Risk', 'Curve', 'Sector', 'Quality', 'Currency', 'Other']
      },
      yAxis: {
        title: {
          text: 'Risk Value'
        }
      },
      tooltip: {
        formatter: function(): string {
          return `<b>${(this as any).x}</b><br/>${(this as any).series.name}: ${(this as any).y.toFixed(4)}`
        }
      },
      plotOptions: {
        column: {
          dataLabels: {
            enabled: false,
          }
        }
      },
      series: [
        {
          name: 'Portfolio Before',
          color: SEMANTIC_COLORS.PORTFOLIO_BEFORE,
          data: [
            Number(beforeData.total_risk.toFixed(4)),
            Number(beforeData.yield_risk.toFixed(4)),
            Number(beforeData.sector_risk.toFixed(4)),
            Number(beforeData.quality_risk.toFixed(4)),
            Number(beforeData.currency_risk.toFixed(4)),
            Number(beforeData.other_risk.toFixed(4))
          ]
        },
        {
          name: 'Portfolio After',
          color: SEMANTIC_COLORS.PORTFOLIO_AFTER,
          data: [
            Number(afterData.total_risk.toFixed(4)),
            Number(afterData.yield_risk.toFixed(4)),
            Number(afterData.sector_risk.toFixed(4)),
            Number(afterData.quality_risk.toFixed(4)),
            Number(afterData.currency_risk.toFixed(4)),
            Number(afterData.other_risk.toFixed(4))
          ]
        },
        {
          name: 'Benchmark',
          color: SEMANTIC_COLORS.BENCHMARK,
          data: [
            Number(beforeData.total_risk_b.toFixed(4)),
            Number(beforeData.yield_risk_b.toFixed(4)),
            Number(beforeData.sector_risk_b.toFixed(4)),
            Number(beforeData.quality_risk_b.toFixed(4)),
            Number(beforeData.currency_risk_b.toFixed(4)),
            Number(beforeData.other_risk_b.toFixed(4))
          ]
        }
      ]
    }
  }, [data])

  if (loading) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Typography variant="h6" color="text.secondary">
          Loading Risk Data...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    )
  }

  return (
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
        />
      </Box>
  )
}

export default RiskData